import { Injectable, isDevMode } from '@angular/core';
import { environment } from '../../../environments/environment';
import { isInDisApeer } from '../../../utils/route/route-utils';
import { LocalStorageItem } from '../models/local-storage-item.model';
import { WindowRef } from './window.service';

export const cziConversionFeature = 'cziConversionFeature';
export const activateLimitCheckingFeature = 'activateLimitChecking';

@Injectable({
    providedIn: 'root',
})
export class ExperimentalFeaturesService {
    private readonly localStoragePrefix = 'experimental_feature_';
    private readonly separator = '_';

    experimentalFeatures: LocalStorageItem[] = [
        {
            identifier: 'inlineManual',
            label: 'Activate Inline Manual (Tutorials)',
            defaultSetting: true,
        },
        {
            identifier: 'activate-maintenance',
            label: 'Activate Maintenance Mode',
            defaultSetting: false,
        },
        {
            identifier: cziConversionFeature,
            label: 'CZI Conversion',
            defaultSetting: true,
        },
        {
            identifier: activateLimitCheckingFeature,
            label: 'Activate Limit Checking',
            defaultSetting: true,
        },
        {
            identifier: 'activateAIModelApiKeys',
            label: 'Activate generating API keys for AI Registry',
            defaultSetting: false,
        },
        {
            identifier: 'fileRename',
            label: 'Renaming file',
            defaultSetting: false,
        },
        {
            identifier: 'instanceSegmentationReleased',
            label: 'Instance segmentation released',
            defaultSetting: true,
        },
        {
            identifier: 'extendTrial',
            label: 'Extending trial',
            defaultSetting: true,
        },
        {
            identifier: 'MEDProject',
            label: 'MED project',
            defaultSetting: environment.MED ? environment.MED : false,
        },
    ];

    static readonly plainJsonContentType = { 'Content-Type': 'application/json' };

    constructor(private windowRef: WindowRef) {
        if (environment.MED) {
            this.initializeMedFeatures();
        }
    }

    get allExperimentalFeatures(): LocalStorageItem[] {
        return this.experimentalFeatures;
    }

    get areInlineManualActivated(): boolean {
        return this.isFeatureActivated('inlineManual');
    }

    get isMaintenanceModeActive(): boolean {
        return this.isFeatureActivated('activate-maintenance');
    }

    get areExperimentalFeaturesEnabled(): boolean {
        return isDevMode() || isInDisApeer(this.windowRef.nativeWindow.location.href);
    }

    get isCziConversionFeatureEnabled(): boolean {
        return this.isFeatureActivated(cziConversionFeature);
    }

    get isLimitCheckingActive(): boolean {
        return this.isFeatureActivated('activateLimitChecking');
    }

    get isApiKeysForAIModelsActive(): boolean {
        return this.isFeatureActivated('activateAIModelApiKeys');
    }

    get isFileRenameActive(): boolean {
        return this.isFeatureActivated('fileRename');
    }

    get isInstanceSegmentationReleasedActive(): boolean {
        return this.isFeatureActivated('instanceSegmentationReleased');
    }

    get isExtendTrialActive(): boolean {
        return this.isFeatureActivated('extendTrial');
    }

    get isMEDProjectActive(): boolean {
        return this.isFeatureActivated('MEDProject');
    }

    private initializeMedFeatures(): void {
        const medFeatureIdentifier = 'MEDProject';
        const localStorageKey = this.localStoragePrefix + this.separator + medFeatureIdentifier;

        // Get the stored value from localStorage
        const storedValue = localStorage.getItem(localStorageKey);

        // Determine the desired value based on environment.MED
        const featureValue = Boolean(environment.MED);
        // If there's no stored value, initialize it with the environment value
        if (storedValue === null) {
            localStorage.setItem(localStorageKey, String(featureValue));
        }
    }

    setExperimentalFeature(featureIdentifier: string, state: boolean): void {
        const localStorageKey = this.localStoragePrefix + this.separator + featureIdentifier;
        localStorage.setItem(localStorageKey, state.toString());
    }

    isFeatureActivated(featureIdentifier: string): boolean {
        let isFeatureActivated: boolean = false;
        const localStorageKey = this.localStoragePrefix + this.separator + featureIdentifier;
        const storedValue = localStorage.getItem(localStorageKey);

        if (storedValue != null) {
            isFeatureActivated = storedValue === 'true';
        } else {
            const feature: LocalStorageItem | undefined = this.getFeatureObject(featureIdentifier);
            if (feature != null) {
                isFeatureActivated = feature.defaultSetting;
            }
        }
        return isFeatureActivated;
    }

    private getFeatureObject(featureIdentifier: string): LocalStorageItem | undefined {
        return this.experimentalFeatures.find(feature => feature.identifier === featureIdentifier);
    }
}
